import React from 'react'
import { Analytics } from "@vercel/analytics/react"
import { Helmet } from 'react-helmet'
import { Header, Footer } from "./LandingPage"
import { Calendar, Clock, ChevronDown } from "lucide-react"
import SEO from './common/SEO'
import { getBlogPosts } from '../lib/blog-data'
import type { BlogPost } from '../lib/blog-posts'
import { BASE_URL } from '../lib/routes'
import { cn } from '../lib/utils'
import { useParams, Link } from 'react-router-dom'

function useActiveHeading(toc: BlogPost['toc']) {
  const [activeId, setActiveId] = React.useState<string>('');

  React.useEffect(() => {
    if (toc.length === 0) return;
    const headings = toc
      .map(item => document.getElementById(item.slug))
      .filter((el): el is HTMLElement => el !== null);
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-110px 0px -70% 0px', threshold: 0 }
    );
    headings.forEach(h => observer.observe(h));
    return () => observer.disconnect();
  }, [toc]);

  return activeId;
}

function scrollToId(e: React.MouseEvent, id: string) {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - 100;
  window.scrollTo({ top: y, behavior: 'smooth' });
  history.replaceState(null, '', `#${id}`);
}

const TableOfContents: React.FC<{ toc: BlogPost['toc'] }> = ({ toc }) => {
  const activeId = useActiveHeading(toc);
  if (toc.length === 0) return null;

  return (
    <nav className="hidden lg:block sticky top-28 self-start w-56 shrink-0 pt-2">
      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">On this page</div>
      <div className="relative">
        <div className="absolute left-0 top-1 bottom-1 w-px bg-border" />
        {toc.map(item => {
          const isActive = activeId === item.slug;
          return (
            <a
              key={item.slug}
              href={`#${item.slug}`}
              onClick={e => scrollToId(e, item.slug)}
              className={cn(
                "relative block py-1.5 pl-4 text-sm leading-snug transition-colors",
                item.level === 3 && "pl-7",
                isActive ? "text-[#1F80FF] font-semibold" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span
                className={cn(
                  "absolute left-[-3px] top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full transition-colors",
                  isActive ? "bg-[#1F80FF]" : "bg-border"
                )}
              />
              {item.text}
            </a>
          );
        })}
      </div>
    </nav>
  );
};

const MobileTableOfContents: React.FC<{ toc: BlogPost['toc'] }> = ({ toc }) => {
  const activeId = useActiveHeading(toc);
  if (toc.length === 0) return null;

  return (
    <details className="lg:hidden group mb-8 rounded-2xl border border-border bg-gray-50/80">
      <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-bold">
        On this page
        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-4 pb-4 pt-1">
        {toc.map(item => {
          const isActive = activeId === item.slug;
          return (
            <a
              key={item.slug}
              href={`#${item.slug}`}
              onClick={e => scrollToId(e, item.slug)}
              className={cn(
                "block py-1.5 text-sm leading-snug transition-colors",
                item.level === 3 && "pl-4",
                isActive ? "text-[#1F80FF] font-semibold" : "text-muted-foreground"
              )}
            >
              {item.text}
            </a>
          );
        })}
      </div>
    </details>
  );
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = React.useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = React.useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function loadPost() {
      if (!slug) return;
      try {
        const posts = await getBlogPosts();
        const foundPost = posts.find(p => p.slug === slug);
        setPost(foundPost || null);
        if (foundPost) {
          setRelatedPosts(
            posts
              .filter(p => p.slug !== foundPost.slug && p.category === foundPost.category)
              .slice(0, 3)
          );
        }
      } catch (error) {
        console.error('Error loading post:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadPost();
  }, [slug]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!post) {
    return <div>Post not found</div>;
  }

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO
        path={`/blog/${post.slug}`}
        title={post.meta.title}
        description={post.meta.description}
        image={post.coverImage}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.meta.description,
            image: post.coverImage.startsWith('http') ? post.coverImage : `${BASE_URL}${post.coverImage}`,
            datePublished: post.date,
            author: {
              "@type": "Person",
              name: "Latisha",
              description: "AOW diver, diving since 2024, building Currents"
            },
            publisher: {
              "@type": "Organization",
              name: "Currents",
              logo: {
                "@type": "ImageObject",
                url: `${BASE_URL}/currents-logo.png`
              }
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${BASE_URL}/blog/${post.slug}`
            }
          })}
        </script>
      </Helmet>
      <Header />
      <main className="flex-1 pt-24">
        <div className="container max-w-5xl flex gap-12 justify-center">
        <article className="max-w-3xl w-full min-w-0">
          <header className="mb-8">
            <div className="mb-4">
              <span className="text-sm font-medium text-[#1F80FF]">{post.category}</span>
            </div>
            <h1 className="text-4xl font-black tracking-tighter sm:text-5xl mb-4 leading-[0.95]">
              {post.title}
            </h1>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">By Latisha</span>
              <span className="text-muted-foreground/60">AOW diver, diving since 2024, building Currents</span>
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {post.date}
              </span>
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {post.readTime}
              </span>
            </div>
          </header>
          
          <div className="mb-8">
            <div className="aspect-[16/9] overflow-hidden rounded-2xl">
              <img 
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
            {post.imageCredit && (
              <p className="text-sm text-muted-foreground mt-2 text-center italic">
                {post.imageCredit}
              </p>
            )}
          </div>

          <MobileTableOfContents toc={post.toc} />

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h1:text-4xl prose-h1:mb-8
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:leading-relaxed prose-p:mb-6
              prose-li:my-2
              prose-strong:font-semibold
              prose-blockquote:border-l-4 prose-blockquote:border-[#1F80FF]
              prose-blockquote:pl-4 prose-blockquote:italic
              prose-a:text-[#1F80FF] prose-a:no-underline hover:prose-a:underline
              prose-img:rounded-xl
              prose-pre:bg-gray-50 prose-pre:p-4 prose-pre:rounded-lg
              prose-code:text-[#1F80FF] prose-code:bg-gray-50 prose-code:px-1 prose-code:rounded
              prose-table:border-collapse
              prose-th:border prose-th:border-gray-200 prose-th:p-2 prose-th:bg-gray-50
              prose-td:border prose-td:border-gray-200 prose-td:p-2"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {relatedPosts.length > 0 && (
            <div className="mt-16 border-t border-border pt-12">
              <h2 className="text-2xl font-bold mb-6">Related Posts</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedPosts.map(related => (
                  <Link key={related.slug} to={`/blog/${related.slug}`} className="group">
                    <div className="aspect-[16/9] overflow-hidden rounded-xl mb-3">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <h3 className="font-semibold leading-snug group-hover:text-[#1F80FF] transition-colors">
                      {related.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
        <TableOfContents toc={post.toc} />
        </div>
      </main>
      <Footer />
      <Analytics />
    </div>
  )
}

export default BlogPostPage 